/**
 * Responsibility: let the administrator manage the hub's own credentials.
 *
 * **Masked until asked, and asking costs a sign-in.** The list never carries a
 * value - the hub sends the last four characters, enough to tell two keys
 * apart and to confirm a paste worked, useless to somebody reading over a
 * shoulder. Revealing one is a deliberate act on a single named secret, and
 * the hub refuses it unless the session was re-authenticated within the last
 * few minutes.
 *
 * The 401 carrying `reauth_required` is the mechanism: rather than asking for
 * a password up front - which would be one more credential needing exactly the
 * protection this page exists to provide - the hub refuses, the page asks
 * Google to confirm, and the request is retried once. So the prompt appears
 * when it is actually needed, not on every visit.
 *
 * **Changes take effect on the next restart**, which the page says plainly.
 * Applying one live would mean rebuilding the service graph underneath
 * in-flight requests, and a credential that changed halfway through a poll is
 * harder to reason about than one that changed on a restart.
 */

import { useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ReauthRequiredError, hubApi, type AdminSecret } from '../../api/client';
import { reauthenticate, secondsSinceSignIn } from '../../auth/firebase';

const KEY = ['admin', 'secrets'];

/**
 * Whether a failure was the hub asking the caller to prove who they are.
 *
 * The error type, not its wording. This used to match on the message
 * containing "401" or "confirm it is you", and the message it actually
 * received said "Your session has ended" - so the popup never opened and
 * every save failed silently.
 */
function needsReauth(error: unknown): boolean {
  return error instanceof ReauthRequiredError;
}

/**
 * Run something, and if the hub asks for a fresh sign-in, get one and retry.
 *
 * **The fallback, not the main path.** By the time this runs, a request has
 * already been awaited, and a browser will not open a popup once it no longer
 * believes it is inside the click that asked for one - so the popup here is
 * liable to be blocked. `confirmThenRun` below is what normally prevents this
 * being reached at all; this stays for the cases it cannot predict, such as a
 * clock that disagrees with the server.
 *
 * Once only. A second refusal after a successful re-authentication means
 * something other than staleness is wrong, and retrying in a loop would put a
 * popup in front of somebody repeatedly without ever explaining why.
 */
async function withReauth<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (!needsReauth(error)) throw error;
    await reauthenticate();
    return action();
  }
}

/**
 * Prove who you are *first*, then do the thing.
 *
 * Changing a credential needs a sign-in from the last few minutes. Finding
 * that out from the server means the popup would open after an awaited
 * request, which browsers block - so the staleness is checked here, from the
 * cached token, while the click is still the reason anything is happening.
 *
 * The window comes from the hub rather than being repeated here, so the two
 * cannot drift apart.
 */
async function confirmThenRun(
  action: () => void,
  windowSeconds: number,
  onProblem: (error: unknown) => void,
): Promise<void> {
  try {
    // A margin, because the check here and the check on the server happen a
    // round trip apart and a save should not fail by two seconds.
    if ((await secondsSinceSignIn()) > Math.max(windowSeconds - 30, 30)) {
      await reauthenticate();
    }
  } catch (error) {
    onProblem(error);
    return;
  }
  action();
}

function SourceChip({ source }: { source: string }) {
  if (source === 'environment') {
    return (
      <Tooltip title="Set in the environment on the host, which overrides anything stored here. Changing it below will have no effect until that variable is removed.">
        <Chip size="small" color="warning" variant="outlined" label="from environment" />
      </Tooltip>
    );
  }
  if (source === 'unset') {
    return <Chip size="small" variant="outlined" label="not set" />;
  }
  return <Chip size="small" color="success" variant="outlined" label="stored" />;
}

function SecretRow({
  secret,
  onEdit,
  onReveal,
  onDelete,
}: {
  secret: AdminSecret;
  onEdit: () => void;
  onReveal: () => void;
  onDelete: () => void;
}) {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">{secret.label}</Typography>
            <SourceChip source={secret.source} />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', mt: 0.25 }}
          >
            {secret.configured ? secret.masked : 'not configured'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Reveal — asks you to confirm it is you">
            <span>
              <IconButton size="small" onClick={onReveal} disabled={!secret.configured}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Change">
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <span>
              <IconButton size="small" onClick={onDelete} disabled={!secret.configured}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {secret.without} Get one from {secret.source_hint}.
      </Typography>
    </Box>
  );
}

/**
 * How long a sign-in counts as fresh, when the hub has not said.
 *
 * Only a fallback for a reply that predates the field. The hub is the
 * authority and sends its own value; being wrong here costs one failed save
 * and a popup, not a lockout.
 */
const ASSUMED_REAUTH_SECONDS = 300;

export function SecretsCard({ reauthSeconds }: { reauthSeconds?: number }) {
  const reauthWindow = reauthSeconds ?? ASSUMED_REAUTH_SECONDS;
  const client = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: KEY, queryFn: hubApi.adminSecrets });

  const [editing, setEditing] = useState<AdminSecret | null>(null);
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState<{ label: string; value: string } | null>(null);
  const [showDraft, setShowDraft] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = () => client.invalidateQueries({ queryKey: KEY });
  const fail = (error: unknown) =>
    setProblem(error instanceof Error ? error.message : String(error));

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      withReauth(() => hubApi.setSecret(key, value)),
    onSuccess: () => {
      setEditing(null);
      setDraft('');
      setProblem(null);
      void refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (key: string) => withReauth(() => hubApi.deleteSecret(key)),
    onSuccess: () => {
      setProblem(null);
      void refresh();
    },
    onError: fail,
  });

  const reveal = useMutation({
    mutationFn: (secret: AdminSecret) =>
      withReauth(() => hubApi.revealSecret(secret.key)).then((got) => ({
        label: secret.label,
        value: got.value,
      })),
    onSuccess: (got) => {
      setProblem(null);
      setRevealed(got);
    },
    onError: fail,
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Credentials</Typography>
          <Typography variant="body2" color="text.secondary">
            The keys this hub uses to reach eBay, PSA, Best Buy and your notification
            channels. Stored in Firebase, so a rebuilt server only needs its service
            account to find them again. Changes take effect when the hub next restarts.
          </Typography>
        </Box>
        <Divider />

        {isLoading && <LinearProgress />}
        {isError && (
          <Alert severity="warning">
            Credentials could not be read. The analysis service may be offline.
          </Alert>
        )}
        {problem && (
          <Alert severity="error" onClose={() => setProblem(null)}>
            {problem}
          </Alert>
        )}

        {data?.map((secret, index) => (
          <Box key={secret.key}>
            {index > 0 && <Divider />}
            <SecretRow
              secret={secret}
              onEdit={() => {
                setEditing(secret);
                setDraft('');
                // Never carry a revealed state into the next credential.
                setShowDraft(false);
              }}
              onReveal={() => reveal.mutate(secret)}
              onDelete={() => remove.mutate(secret.key)}
            />
          </Box>
        ))}
      </Stack>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editing?.without} Get one from {editing?.source_hint}.
          </DialogContentText>
          {editing?.source === 'environment' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              An environment variable on the host is overriding this. Saving here will
              store the value but change nothing until that variable is removed.
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            // Hidden by default, because a credential left visible ends up in
            // a screenshot eventually - but revealable, because typing a
            // forty-character key blind and having it silently be wrong is
            // worse. The choice belongs to whoever is at the keyboard.
            type={showDraft ? 'text' : 'password'}
            label="New value"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: 4096,
                spellCheck: false,
                // Never offer to remember a credential, and never autocorrect
                // one.
                autoComplete: 'off',
                autoCapitalize: 'off',
                autoCorrect: 'off',
                style: showDraft ? { fontFamily: 'monospace' } : undefined,
              },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showDraft ? 'Hide' : 'Show what I am typing'}>
                      <IconButton
                        edge="end"
                        onClick={() => setShowDraft((shown) => !shown)}
                        aria-label={showDraft ? 'Hide the value' : 'Show the value'}
                      >
                        {showDraft ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!draft.trim() || save.isPending}
            onClick={() => {
              if (!editing) return;
              void confirmThenRun(
                () => save.mutate({ key: editing.key, value: draft.trim() }),
                reauthWindow,
                fail,
              );
            }}
          >
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revealed !== null} onClose={() => setRevealed(null)} fullWidth maxWidth="sm">
        <DialogTitle>{revealed?.label}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            value={revealed?.value ?? ''}
            slotProps={{ htmlInput: { readOnly: true, style: { fontFamily: 'monospace' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealed(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
